import { graphql } from "@repo/graphql-types/gql";
import Left from "@repo/icons/default/Left";
import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { z } from "zod";
import { useAtom } from "jotai";
import { useEffect, type JSX } from "react";
import type { FragmentType } from "@repo/graphql-types";
import { useResetAtom } from "jotai/utils";
import { fetchQuery } from "@/hooks/use-graphql";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import { appointmentFormAtom } from "@/lib/atoms/appointment-form-atom";
import { FullPageSpinner } from "@/components/ui/loading/full-page-spinner";
import {
  type BlockedActionsListAlertFragment,
  BlockedAppointmentAlert,
} from "@/components/appointment/blocked-appointment-alert";
import { convertDateTimeIgnoringTimezone, formatWithZonedDate } from "@/lib/helpers/date";
import {
  RescheduleAppointmentForm,
  type RescheduleAppointmentFormFragment,
} from "@/components/appointment/form/reschedule-appointment-form";

const ReschedulePageQuery = graphql(`
  query ReschedulePageQuery($dataInicio: date!, $codAgendamento: Int!) {
    ...RescheduleAppointmentFormFragment
    ...BlockedActionsListAlertFragment

    agendamento: agendamentos_by_pk(codAgendamento: $codAgendamento) {
      codUnidade
      codUsuarioCompromisso
      codUsuarioFormaRecebimento
      data
      codAgendamento
    }
  }
`);

const ReschedulePage = (): JSX.Element => {
  const loaderData = Route.useLoaderData().data;

  const [appointmentFormAtomValue, setAppointmentFormAtomValue] =
    useAtom(appointmentFormAtom);

  const setInitialValues = (): void => {
    if (loaderData?.agendamento) {
      const {
        codUnidade,
        codUsuarioCompromisso,
        codUsuarioFormaRecebimento,
        data,
        codAgendamento,
      } = loaderData.agendamento;

      const { appointmentDate, appointmentType, paymentMethod, unit, appointmentId } =
        appointmentFormAtomValue;

      if (!appointmentId || appointmentId !== codAgendamento) {
        setAppointmentFormAtomValue({
          ...appointmentFormAtomValue,
          appointmentDate: convertDateTimeIgnoringTimezone(data),
          appointmentType: codUsuarioCompromisso,
          paymentMethod: codUsuarioFormaRecebimento,
          unit: codUnidade,
          appointmentId: codAgendamento,
        });
        return;
      }

      setAppointmentFormAtomValue({
        ...appointmentFormAtomValue,
        appointmentDate: appointmentDate ?? convertDateTimeIgnoringTimezone(data),
        appointmentType: appointmentType || codUsuarioCompromisso,
        paymentMethod: paymentMethod || codUsuarioFormaRecebimento,
        unit: unit || codUnidade,
        appointmentId,
      });
    }
  };

  const isPending = !appointmentFormAtomValue.appointmentId;

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy code
  useEffect(() => {
    setInitialValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Deve ser executado apenas na montagem do componente
  }, []);

  const router = useRouter();

  const resetRescheduleAppointmentForm = useResetAtom(appointmentFormAtom);

  const onBackButtonClick = (): void => {
    resetRescheduleAppointmentForm();
    router.history.back();
  };

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" onClick={onBackButtonClick} />
        <HeaderTitle title="Reagendamento" align="center" />
      </HeaderRoot>

      <Page>
        {isPending ? (
          <FullPageSpinner />
        ) : (
          <>
            <div className="pb-4">
              <BlockedAppointmentAlert
                blockedActionsListFragmentData={
                  loaderData as FragmentType<typeof BlockedActionsListAlertFragment>
                }
              />
            </div>
            <RescheduleAppointmentForm
              loaderData={
                loaderData as FragmentType<typeof RescheduleAppointmentFormFragment>
              }
            />
          </>
        )}
      </Page>
    </>
  );
};

export const Route = createFileRoute("/appointment/$appointmentId/reschedule")({
  component: ReschedulePage,
  parseParams: (params) => ({
    appointmentId: z.int().parse(Number(params.appointmentId)),
  }),
  loader: async (opts) => {
    try {
      const variables = {
        dataInicio: formatWithZonedDate(new Date(), "yyyy-MM-dd"),
        codAgendamento: opts.params.appointmentId,
      };

      const data = await fetchQuery(opts.context, ReschedulePageQuery, variables);

      if (!data.agendamento) throw new Error("Appointment not found!");

      return { data };
    } catch (_e) {
      redirect({
        to: "/calendar",
        throw: true,
      });
    }

    return {};
  },
});
